import { IconCircleInfo } from 'central-icons/IconCircleInfo';
import React, { forwardRef, useState } from 'react';
import type { NodeApi } from 'react-arborist';

import { type NodeData, Popover, Text, type TreeData } from '@revelio/ui';

import { useCompanyCompetitors } from '../../hooks/useCompanyCompetitors';
import { useCompanyEntityInfo } from '../../hooks/useCompanyEntityInfo';
import { usePersistentlyPlacedPopover } from '../../hooks/usePersistentlyPlacedPopover';
import { useCompanySelection } from './company-selection-context';
import { EntityInfoContent } from './EntityInfoContent';
import styles from './EntityInfoPopover.module.css';
import { EntityInfoSkeleton } from './EntityInfoSkeleton';
import { useEntityInfoOpenStore } from './entity-info-open-context';
import layoutStyles from './entity-info-popover.module.css';
import { trackEntityInfoInteraction } from './entity-info-tracking';
import { TopCompaniesContent } from './TopCompaniesContent';

export type EntityInfoData = {
  ult_parent_name?: string;
  ult_parent_rcid?: number;
  hasUltimateParent?: boolean;
  primary_website?: string;
  primary_linkedin_url?: string;
  rics_k400?: number;
  city?: string;
  state?: string;
  country?: string;
  industry?: string;
  rcid?: number;
  top_companies?: string[];
};

export type EntityInfoPopoverProps = {
  node: NodeApi<TreeData<EntityInfoData>>;
};

/** Trailing numeric segment (the rcid) of a company tree node id, or null. */
const parseLeafRcid = (id: string): number | null => {
  const seg = id.split('_').pop()?.split(':').pop() ?? '';
  const n = Number.parseInt(seg, 10);
  return Number.isFinite(n) ? n : null;
};

const ENTITY_INFO_POPOVER_TESTID = 'entity-info-popover';

/**
 * Whether a DOM event originates inside a portaled entity-info tooltip. The
 * tooltip is rendered on `document.body`, outside the tree and its drill-down
 * popouts, so those surfaces' outside-click handlers would otherwise treat a
 * click in the tooltip (e.g. "View Competitors") as a click away and collapse
 * themselves. They call this to ignore such clicks.
 */
export const isEventInsideEntityInfoPopover = (event: Event): boolean =>
  Boolean(
    (event.target as HTMLElement | null)?.closest?.(
      `[data-testid="${ENTITY_INFO_POPOVER_TESTID}"]`
    )
  );

export const EntityInfoPopover = forwardRef<
  HTMLDivElement,
  EntityInfoPopoverProps
>(({ node }, ref) => {
  const data: NodeData<EntityInfoData> = node.data;
  const { entityInfo, name: nodeName } = data;

  // Persist the open state across the tree's row remounts (see
  // entity-info-open-context): if this row's tooltip was open before a remount,
  // reopen it so a company toggle from inside the tooltip doesn't close it.
  const openStore = useEntityInfoOpenStore();
  const nodeId = data.id;
  const wasOpenBeforeRemount =
    openStore != null &&
    nodeId != null &&
    openStore.openIdRef.current === nodeId;
  const [isPopoverOpen, setIsPopoverOpen] = useState(wasOpenBeforeRemount);

  // Reposition the portaled tooltip when its content grows (e.g. expanding the
  // competitors list), so it stays on screen instead of being pushed off the
  // top edge — a ResizeObserver nudges Chakra's popper to re-flip/re-place.
  const { contentRef } = usePersistentlyPlacedPopover(isPopoverOpen);

  // Search-mode nodes carry `entityInfo`. Browse-mode company leaves don't —
  // they only have an id whose trailing segment is the rcid, so fetch their
  // detail by rcid (via /get-company) on open. Non-company browse nodes (k10/k50
  // industry groups) aren't leaves, so they resolve to no rcid and render nothing.
  const derivedRcid =
    entityInfo?.rcid ?? (node.isLeaf ? parseLeafRcid(data.id) : null);
  const {
    entityInfo: fetchedEntityInfo,
    isLoading,
    isFetched,
  } = useCompanyEntityInfo(derivedRcid, {
    enabled: isPopoverOpen && !entityInfo && derivedRcid != null,
  });
  const resolvedEntityInfo = entityInfo ?? fetchedEntityInfo;

  // The "+"/competitors add flow is scoped to the company filter: it needs the
  // selection add-context, which the other tooltip surfaces don't provide.
  const showAddV2 = useCompanySelection() != null;

  // Runs alongside /get-company rather than waiting for `CompetitorsSection` to
  // mount, so the "View Competitors" line's presence is usually settled before
  // the card paints. That section's own `cache-first` read resolves from this.
  const { competitors, isLoading: isLoadingCompetitors } =
    useCompanyCompetitors(derivedRcid, {
      enabled: isPopoverOpen && showAddV2 && derivedRcid != null,
    });

  // Mirrors `CompetitorsSection`, which holds the link open while the list is
  // pending and drops it for a company that has none — so the placeholder
  // reserves that row on the same terms.
  const showCompetitorsRow =
    showAddV2 && (isLoadingCompetitors || competitors.length > 0);

  React.useImperativeHandle(ref, () => contentRef.current as HTMLDivElement);

  // Not a company: no pre-supplied entityInfo and no rcid to fetch one.
  if (!entityInfo && derivedRcid == null) {
    return null;
  }

  // Only once the lookup has settled empty is the bare name all there is to
  // show. Every earlier state renders the skeleton instead — falling through to
  // the name while the fetch is still in flight opens the tooltip as a small
  // title box that then grows into the card.
  const lookupFoundNothing =
    isFetched && !isLoading && fetchedEntityInfo == null;

  const renderBody = () => {
    if (resolvedEntityInfo) {
      if (resolvedEntityInfo.top_companies?.length) {
        return (
          <TopCompaniesContent
            companies={resolvedEntityInfo.top_companies}
            nodeName={nodeName}
          />
        );
      }
      return (
        <EntityInfoContent
          nodeName={nodeName}
          entityInfo={resolvedEntityInfo}
          showAddV2={showAddV2}
        />
      );
    }
    if (lookupFoundNothing) {
      return (
        <Text fontSize="sm" weight="semibold" className={styles.title}>
          {nodeName}
        </Text>
      );
    }
    return (
      <EntityInfoSkeleton
        nodeName={nodeName}
        showCompetitorsRow={showCompetitorsRow}
      />
    );
  };

  return (
    <Popover
      openOnHover
      // Open below the trigger so expanding the competitors list grows the
      // tooltip downward into the space below (search rows sit near the top)
      // rather than flipping it up-and-off-screen — and so the growth doesn't
      // move the tooltip out from under the pointer and close it. Radix still
      // flips it above for a trigger near the viewport bottom.
      placement="bottom"
      closeDelay={100}
      openDelay={500}
      arrow
      sideOffset={12}
      className={styles.content}
      data-testid={ENTITY_INFO_POPOVER_TESTID}
      defaultOpen={wasOpenBeforeRemount}
      onOpenChange={(nextOpen) => {
        setIsPopoverOpen(nextOpen);
        if (nextOpen) {
          if (openStore && nodeId != null) openStore.openIdRef.current = nodeId;
          // A hover-open by the reader; the remount reopen never lands here.
          trackEntityInfoInteraction('opened', { can_add: showAddV2 });
          return;
        }
        // A real close (mouse leave) — not a remount, which never fires this.
        // Reset the persisted open + expanded state so a later reopen starts
        // fresh (collapsed).
        if (openStore && openStore.openIdRef.current === nodeId) {
          openStore.openIdRef.current = null;
          openStore.expandedRcidRef.current = null;
          openStore.reportedParentRcidRef.current = null;
        }
      }}
      trigger={
        <div className={layoutStyles.infoTrigger}>
          <IconCircleInfo
            size="1em"
            className={styles.infoIcon}
            data-open={isPopoverOpen || undefined}
            onClick={(e) => e.stopPropagation()}
            aria-label="Company info"
            data-testid="entity-info-trigger"
          />
        </div>
      }
    >
      {/* Never taller than the viewport; the body scrolls inside the tooltip
          rather than overflowing off the top/bottom on a short screen. The
          scroll sits here rather than on the surface because any `overflow`
          other than `visible` there clips the arrow, which sits outside the
          box. This element is also what the tree row's click handler tests
          with `contains()`, so it has to wrap the whole card. */}
      <div ref={contentRef} className={styles.body}>
        {renderBody()}
      </div>
    </Popover>
  );
});
